import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Select,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from "@chakra-ui/react";
import PhoneStatusBadge from "./sms/PhoneStatusBadge";
import PatientPhones from "./sms/PatientPhones";
import { getBackendURL } from "./env";
import { isConfirmationOpen, ruleLabel } from "./AppointmentConfirmationModal";
import {
  fetchConfirmationSchedule,
  fetchConfirmationSettings,
  cancelConfirmations,
  smsOffWarning,
  shiftDateString,
} from "../utils/confirmationScheduling";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";
import { confirmBadNumbers } from "../utils/badNumbers";
import { rowPhones } from "../utils/smsPhone";

// The "Confirmation reminders" tab of the Send Reminders modal: the form
// reminders tab, section for section, wired to appointment confirmations.
// Sends go through /twilio/send-confirmations/ — immediately, or queued as
// manual `confirmation` rows for the worker when scheduling is on.
//
// Renders its own ModalBody + ModalFooter, so it must sit inside a Modal.

const ACTIVE = new Set(["scheduled", "processing"]);
const STATUS_COLOR = { scheduled: "blue", processing: "purple", sent: "green", cancelled: "orange", failed: "red" };
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const statusOf = (p) => String(p?.confirmation_status || "").toUpperCase();
// Any number the patient can be texted on — cell or home (utils/smsPhone).
const hasPhone = (p) => rowPhones(p).length > 0;
const hasEmail = (p) => /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(String(p?.email || "").trim());
// Open AND reachable by text or email. A row with only an email is asked
// by email, automatically and by Send Confirmations alike.
const isSelectable = (p) => isConfirmationOpen(p) && (hasPhone(p) || hasEmail(p));
const typeOf = (p) => String(p?.type || "").toUpperCase().trim();
const nameOf = (p) => p?.patient_name || p?.name || "Unknown Patient";
const newRowId = () => `c_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const formatSendTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "N/A" : d.toLocaleString();
};

export const confirmationBadge = (p) => {
  const status = statusOf(p);
  if (status === "C") return { label: "CONFIRMED", colorScheme: "green" };
  if (status === "R") return { label: "RESCHEDULE", colorScheme: "orange" };
  if (status === "U" || p?.confirmation_sent_at) return { label: "AWAITING REPLY", colorScheme: "yellow" };
  return { label: "NOT ASKED", colorScheme: "red" };
};

const typeColor = (type) => {
  const t = String(type || "").toUpperCase();
  if (t.includes("FF")) return "purple";
  if (t.includes("NP")) return "green";
  if (t.includes("NS")) return "teal";
  return "gray";
};

const ConfirmationRemindersPanel = ({
  schema,
  date,
  patients = [],
  viewerTimezone,
  onClose,
  onSent,
  refreshKey = 0,
}) => {
  const toast = useToast();
  const [settingsInfo, setSettingsInfo] = useState(null); // { settings, messagingEnabled }
  const [schedule, setSchedule] = useState(null); // { rows, autoStatus, windowDays }
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [selected, setSelected] = useState({});
  const [typeFilter, setTypeFilter] = useState([]);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [sendDates, setSendDates] = useState([]);
  const [sending, setSending] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [cancelling, setCancelling] = useState(null); // row id | "all"
  const [preview, setPreview] = useState(null); // { title, text }
  const [previewing, setPreviewing] = useState(false);
  const [template, setTemplate] = useState("default");

  const schemaName = schema || "public";
  const messagingEnabled = settingsInfo ? settingsInfo.messagingEnabled : null;

  // `patients` can be fanned in from every selected provider schema (the
  // hub is opened over the whole clinic group, not just `schemaName`), so
  // the SMS kill switch, the reminder queue, and sends/cancels below all
  // have to fan out over every schema actually present — a single call
  // scoped to `schemaName` left every OTHER schema's queue, and its own
  // SMS-off state, invisible here. Falls back to `schemaName` alone when
  // nothing is `_schema`-tagged, which keeps single-schema callers
  // identical to the old behavior.
  const scheduleSchemas = useMemo(() => {
    const tagged = [...new Set(patients.map((p) => p?._schema).filter(Boolean))];
    return tagged.length ? tagged : [schemaName];
  }, [patients, schemaName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const infos = await Promise.all(
        scheduleSchemas.map((s) => fetchConfirmationSettings({ schema: s })),
      );
      if (cancelled) return;
      // SMS is effectively off for this hub if it's off for ANY involved
      // schema — a queued confirmation for that schema's patients will fail
      // to send even though other schemas are fine.
      const offSchemas = scheduleSchemas.filter((s, i) => infos[i]?.messagingEnabled === false);
      const anyKnown = infos.some((i) => typeof i?.messagingEnabled === "boolean");
      setSettingsInfo({
        messagingEnabled: anyKnown ? offSchemas.length === 0 : null,
        offSchemas,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [scheduleSchemas, refreshKey]);

  const loadSeq = useRef(0);
  const loadSchedule = useCallback(async () => {
    if (!date) return;
    const seq = ++loadSeq.current;
    setLoadingSchedule(true);
    const results = await Promise.all(
      scheduleSchemas.map((s) => fetchConfirmationSchedule({ schema: s, date })),
    );
    // Overlapping loads (a send followed by a quick cancel) can resolve out
    // of order; only the newest request may apply its result.
    if (seq === loadSeq.current) {
      const rows = results.flatMap((result, i) =>
        (result?.rows || []).map((r) => ({ ...r, _schema: scheduleSchemas[i] })),
      );
      const windowDaysResult = results.find((r) => r?.windowDays != null);
      setSchedule({ rows, windowDays: windowDaysResult?.windowDays ?? null });
      setLoadingSchedule(false);
    }
  }, [scheduleSchemas, date]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule, refreshKey]);

  const stats = useMemo(() => {
    const out = { total: patients.length, confirmed: 0, reschedule: 0, awaiting: 0 };
    patients.forEach((p) => {
      const status = statusOf(p);
      if (status === "C") out.confirmed += 1;
      else if (status === "R") out.reschedule += 1;
      else out.awaiting += 1;
    });
    out.rate = out.total ? Math.round((out.confirmed / out.total) * 100) : 0;
    return out;
  }, [patients]);

  const availableTypes = useMemo(
    () => [...new Set(patients.map(typeOf).filter(Boolean))].sort(),
    [patients],
  );

  const filtered = useMemo(
    () => (typeFilter.length ? patients.filter((p) => typeFilter.includes(typeOf(p))) : patients),
    [patients, typeFilter],
  );

  // Auto-select every selectable patient whenever the shown list changes —
  // the form reminders tab does the same, so a filter change never leaves a
  // hidden patient selected.
  useEffect(() => {
    const next = {};
    filtered.forEach((p) => {
      if (p && p.id && isSelectable(p)) next[p.id] = true;
    });
    setSelected(next);
  }, [filtered]);

  const selectedIds = useMemo(
    () => filtered.filter((p) => p && p.id && selected[p.id] && isSelectable(p)).map((p) => String(p.id)),
    [filtered, selected],
  );
  const selectableCount = useMemo(() => filtered.filter(isSelectable).length, [filtered]);

  const patientById = useMemo(() => {
    const map = {};
    patients.forEach((p) => {
      if (p && p.id) map[String(p.id)] = p;
    });
    return map;
  }, [patients]);

  const rows = schedule?.rows || [];
  const activeRows = rows.filter((r) => ACTIVE.has(String(r.status || "").toLowerCase()));
  const historyRows = rows.filter((r) => !ACTIVE.has(String(r.status || "").toLowerCase()));

  const toggleScheduleMode = (checked) => {
    setScheduleMode(checked);
    if (checked && sendDates.length === 0) {
      setSendDates([{ id: newRowId(), date: shiftDateString(date, -1) || date || "", time: "09:00" }]);
    }
    if (!checked) setSendDates([]);
  };

  const scheduleProblem = () => {
    if (!scheduleMode) return null;
    if (sendDates.length === 0) return 'Add at least one send date using "+ Add another send date".';
    if (sendDates.some((r) => !DATE_RE.test(r.date || ""))) return "Every send date needs a date.";
    const dates = sendDates.map((r) => r.date);
    if (new Set(dates).size !== dates.length) return "Send dates must all be different.";
    return null;
  };

  const send = async () => {
    if (selectedIds.length === 0) {
      toast({
        title: "No Patients Selected",
        description: "Select at least one patient who has not replied yet and has a phone number or email.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const problem = scheduleProblem();
    if (problem) {
      toast({ title: "Check the send dates", description: problem, status: "error", duration: 5000, isClosable: true });
      return;
    }
    // Every candidate number per patient: a patient is only "bad" when all of
    // them are (the backend texts the first number that is not bad).
    const phonesOf = (id) => rowPhones(patientById[id]);
    const badNumberGuard = await confirmBadNumbers(
      selectedIds.map((id) => ({ phones: phonesOf(id), name: nameOf(patientById[id]) })),
    );
    if (!badNumberGuard) return;
    // Known-bad numbers the dialog skipped still go to the backend, WITHOUT
    // confirm_bad_number: the text is refused there and the request is
    // emailed instead (or skipped when there is no email on file).
    const sendIds = selectedIds;
    setSending(true);
    try {
      // Selected patients can belong to different provider schemas (the
      // hub spans every selected provider); each schema's confirmations
      // have to go through its own backend call, or send-confirmations
      // looks the patient ids up in the wrong schema's notes-c and finds
      // nothing (or, worse, a colliding id in the wrong schema).
      const groups = new Map(); // schema -> patient ids[]
      sendIds.forEach((id) => {
        const s = patientById[id]?._schema || schemaName;
        if (!groups.has(s)) groups.set(s, []);
        groups.get(s).push(id);
      });
      const scheduleBody = scheduleMode
        ? sendDates.map((r) => ({ date: r.date, time: r.time || "09:00" }))
        : null;
      const responses = await Promise.all(
        [...groups.entries()].map(async ([s, ids]) => {
          const body = { schema: s, patient_ids: ids };
          const confirmedBad = ids.filter((id) => phonesOf(id).some((ph) => badNumberGuard.confirmed(ph)));
          if (confirmedBad.length) body.confirm_bad_number_ids = confirmedBad;
          if (scheduleBody) body.schedule = scheduleBody;
          const response = await fetch(`${getBackendURL()}/twilio/send-confirmations/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          const payload = await response.json();
          if (!response.ok || payload.status !== "success") {
            throw new Error(payload.error || `HTTP ${response.status}`);
          }
          return payload;
        }),
      );
      const results = responses.flatMap((p) => p.results || []);
      const firstReason = (kind) => (results.find((r) => r.result === kind) || {}).reason;
      const sum = (key) => responses.reduce((a, p) => a + (p[key] || 0), 0);
      const done = scheduleMode ? sum("scheduled") : sum("sent");
      const skipped = sum("skipped");
      const failed = sum("failed");
      const queued = sum("queued");
      const emailed = sum("emailed");
      const verb = scheduleMode ? "Scheduled" : "Sent";
      toast({
        title: `${verb} ${done} confirmation${done === 1 ? "" : "s"}`,
        description:
          `${skipped} skipped, ${failed} failed.` +
          (queued > 0 ? ` ${queued} queued for retry.` : "") +
          (emailed > 0 ? ` ${emailed} emailed instead (number can't take texts).` : "") +
          (failed ? ` First failure: ${firstReason("failed")}.` : "") +
          (done === 0 && skipped > 0 ? ` Reason: ${firstReason("skipped")}.` : ""),
        status: failed ? "warning" : done === 0 ? "info" : "success",
        duration: 7000,
        isClosable: true,
      });
      await loadSchedule();
      if (onSent) await onSent();
    } catch (error) {
      toast({
        title: scheduleMode ? "Failed to schedule confirmations" : "Failed to send confirmations",
        description: error.message,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  // Email the request now to every selected patient with an email on file,
  // whatever their phone — the same email a bad number gets automatically.
  const sendEmails = async () => {
    const ids = selectedIds.filter((id) => hasEmail(patientById[id]));
    if (ids.length === 0) {
      toast({
        title: "No email addresses",
        description: "None of the selected patients has an email address on file.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setEmailing(true);
    try {
      const groups = new Map();
      ids.forEach((id) => {
        const s = patientById[id]?._schema || schemaName;
        if (!groups.has(s)) groups.set(s, []);
        groups.get(s).push(id);
      });
      const responses = await Promise.all(
        [...groups.entries()].map(async ([s, groupIds]) => {
          const response = await fetch(`${getBackendURL()}/twilio/send-email-confirmations/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ schema: s, patient_ids: groupIds }),
          });
          const payload = await response.json();
          if (!response.ok || payload.status !== "success") {
            throw new Error(payload.error || `HTTP ${response.status}`);
          }
          return payload;
        }),
      );
      const results = responses.flatMap((p) => p.results || []);
      const count = (kind) => results.filter((r) => r.result === kind).length;
      const firstReason = (kind) => (results.find((r) => r.result === kind) || {}).reason;
      const emailed = count("emailed");
      const failed = count("failed");
      const skipped = count("skipped") + (selectedIds.length - ids.length);
      toast({
        title: `Emailed ${emailed} confirmation${emailed === 1 ? "" : "s"}`,
        description:
          `${skipped} skipped, ${failed} failed.` +
          (failed ? ` First failure: ${firstReason("failed")}.` : "") +
          (emailed === 0 && count("skipped") ? ` Reason: ${firstReason("skipped")}.` : ""),
        status: failed ? "warning" : emailed === 0 ? "info" : "success",
        duration: 7000,
        isClosable: true,
      });
      await loadSchedule();
      if (onSent) await onSent();
    } catch (error) {
      toast({ title: "Failed to send emails", description: error.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setEmailing(false);
    }
  };

  const previewMessage = async () => {
    const sample =
      filtered.find((p) => p && selected[p.id] && isSelectable(p)) ||
      filtered.find(isSelectable) ||
      filtered[0];
    if (!sample) {
      toast({
        title: "No Patients to Preview",
        description: "Select at least one patient (or change the filter) to preview the confirmation text.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setPreviewing(true);
    try {
      const body = { schema: sample._schema || schemaName, patient_id: sample.id };
      const first = scheduleMode ? sendDates.find((r) => DATE_RE.test(r.date || "")) : null;
      if (first) {
        body.send_date = first.date;
        body.send_time = first.time || "09:00";
      }
      const response = await fetch(`${getBackendURL()}/twilio/confirmation-preview/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      setPreview({ title: `Message preview — ${nameOf(sample)}`, text: payload.message });
    } catch (error) {
      toast({ title: "Could not build the preview", description: error.message, status: "error", duration: 6000, isClosable: true });
    } finally {
      setPreviewing(false);
    }
  };

  const cancelRows = async (ids, label) => {
    setCancelling(label);
    try {
      // Queue rows can come from more than one schema (loadSchedule tags
      // each with `_schema`); cancel each schema's rows through its own
      // call, or the ones from a foreign schema silently fail to match.
      const byId = new Map((schedule?.rows || []).map((r) => [r.id, r]));
      const groups = new Map(); // schema -> row ids[]
      ids.forEach((id) => {
        const s = byId.get(id)?._schema || schemaName;
        if (!groups.has(s)) groups.set(s, []);
        groups.get(s).push(id);
      });
      const counts = await Promise.all(
        [...groups.entries()].map(([s, groupIds]) => cancelConfirmations({ schema: s, ids: groupIds })),
      );
      const n = counts.reduce((a, b) => a + b, 0);
      toast({ title: `Cancelled ${n} scheduled confirmation${n === 1 ? "" : "s"}`, status: "success", duration: 3000 });
      await loadSchedule();
    } catch (error) {
      toast({ title: "Could not cancel", description: error.message, status: "error", duration: 6000, isClosable: true });
    } finally {
      setCancelling(null);
    }
  };

  const renderScheduledRow = (r, { isActive }) => {
    const patient = patientById[String(r.notes_c_id)];
    const status = String(r.status || "").toLowerCase();
    return (
      <Box
        key={r.id}
        p={2}
        bg={isActive ? "white" : "gray.50"}
        border="1px"
        borderColor={isActive ? "blue.200" : "gray.200"}
        borderRadius="md"
        opacity={isActive ? 1 : 0.85}
      >
        <HStack justify="space-between" align="flex-start">
          <VStack align="flex-start" spacing={0} flex={1}>
            <HStack spacing={1} align="center">
              <Text fontWeight="semibold" fontSize="sm">
                {r.patient_name || nameOf(patient)}
              </Text>
              <Badge variant="outline" fontSize="xs">{ruleLabel(r)}</Badge>
              {patient?.type && (
                <Badge colorScheme={typeColor(patient.type)} fontSize="xs" px={2} borderRadius="full">
                  {typeOf(patient)}
                </Badge>
              )}
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Appointment: {patient?.date_ || date || "N/A"}
              {patient?.time_ ? ` at ${convertScheduledTime(patient.date_, patient.time_, patient.timezone_source ?? patient.timezone ?? null, viewerTimezone)}` : ""}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {isActive ? "Will send:" : "Was scheduled:"} {formatSendTime(r.scheduled_for)}
            </Text>
            {r.channel === "email" ? (
              <Text fontSize="xs" color="gray.600">✉️ {r.email}</Text>
            ) : r.phone && (
              <Text fontSize="xs" color="gray.600">📞 {r.phone}<PhoneStatusBadge phone={r.phone} compact ml={1} /></Text>
            )}
            {r.error_message && (
              <Text fontSize="xs" color={status === "failed" ? "red.600" : "gray.500"}>{r.error_message}</Text>
            )}
          </VStack>
          <VStack align="flex-end" spacing={1}>
            <Badge colorScheme={STATUS_COLOR[status] || "gray"}>{status.toUpperCase()}</Badge>
            <HStack spacing={1}>
              <Button
                size="xs"
                variant="outline"
                colorScheme="blue"
                onClick={() => setPreview({ title: `Message preview — ${r.patient_name || nameOf(patient)}`, text: r.message })}
                aria-label={`Preview confirmation ${r.id}`}
              >
                Preview
              </Button>
              {isActive && (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="red"
                  isLoading={cancelling === r.id}
                  onClick={() => cancelRows([r.id], r.id)}
                  aria-label={`Cancel confirmation ${r.id}`}
                >
                  Cancel
                </Button>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Box>
    );
  };

  const renderScheduledList = (list, { isActive, emptyText }) => {
    if (loadingSchedule && schedule === null) {
      return (
        <HStack>
          <Spinner size="sm" />
          <Text fontSize="sm">Loading confirmations...</Text>
        </HStack>
      );
    }
    if (list.length === 0) {
      return (
        <Text fontSize="sm" color="gray.600" fontStyle="italic">{emptyText}</Text>
      );
    }
    return (
      <VStack align="stretch" spacing={2} maxHeight={isActive ? "220px" : "180px"} overflowY="auto">
        {list.map((r) => renderScheduledRow(r, { isActive }))}
      </VStack>
    );
  };

  return (
    <>
      <ModalBody>
        <VStack spacing={6} align="stretch">
          {messagingEnabled === false && (
            <Alert status="warning" borderRadius="md" data-testid="sms-off-warning">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">SMS is turned off</AlertTitle>
                <AlertDescription fontSize="sm">
                  {smsOffWarning(settingsInfo?.offSchemas?.join(", ") || schemaName)}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Summary Stats */}
          <Box p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200" data-testid="confirmation-stats">
            <HStack spacing={6} justify="center">
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="blue.700" data-testid="stat-total">{stats.total}</Text>
                <Text fontSize="sm" color="blue.600">Total Patients</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="green.700" data-testid="stat-confirmed">{stats.confirmed}</Text>
                <Text fontSize="sm" color="green.600">Confirmed</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="red.700" data-testid="stat-awaiting">{stats.awaiting}</Text>
                <Text fontSize="sm" color="red.600">Awaiting Reply</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="purple.700" data-testid="stat-rate">{stats.rate}%</Text>
                <Text fontSize="sm" color="purple.600">Confirmation Rate</Text>
              </VStack>
            </HStack>
            {stats.reschedule > 0 && (
              <Text fontSize="xs" color="orange.700" textAlign="center" mt={2}>
                {stats.reschedule} asked to reschedule — rebook them, then set their status back to U.
              </Text>
            )}
          </Box>

          {/* Contact Method */}
          <FormControl>
            <FormLabel>Contact Method</FormLabel>
            <VStack align="flex-start">
              <Checkbox isChecked isDisabled>Phone/SMS</Checkbox>
              <Text fontSize="xs" color="gray.500">
                Confirmation requests are sent by text; the patient replies C or R. When a
                patient has no phone number, or their number can't take texts, the request is
                emailed instead and they answer by clicking Confirm or Reschedule.
              </Text>
            </VStack>
          </FormControl>

          {/* Scheduling */}
          <FormControl>
            <FormLabel>Scheduling</FormLabel>
            <VStack align="flex-start" spacing={2}>
              <Checkbox isChecked={scheduleMode} onChange={(e) => toggleScheduleMode(e.target.checked)}>
                Schedule confirmations instead of sending immediately
              </Checkbox>
              <Text fontSize="sm" color="gray.600">
                When enabled, confirmation texts are queued now and sent at the
                scheduled day and time (clinic time). Add one or more send dates
                manually — each date must be different. A queued text stops on its
                own if the patient replies first or the appointment moves.
              </Text>
              {scheduleMode && (
                <Box w="100%" mt={1}>
                  {sendDates.length === 0 && (
                    <Text fontSize="xs" color="orange.600" mb={2}>
                      Add at least one send date below before sending.
                    </Text>
                  )}
                  <VStack align="stretch" spacing={2}>
                    {sendDates.map((r, idx) => {
                      const dupDate = sendDates.find((other, otherIdx) => otherIdx !== idx && other.date && other.date === r.date);
                      const isInvalid = !r.date;
                      return (
                        <HStack
                          key={r.id}
                          spacing={2}
                          p={2}
                          borderWidth="1px"
                          borderColor={dupDate || isInvalid ? "red.300" : "gray.200"}
                          borderRadius="md"
                          bg="white"
                          align="flex-end"
                        >
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                              Confirmation #{idx + 1} — Send on date
                            </Text>
                            <Input
                              type="date"
                              size="sm"
                              value={r.date}
                              aria-label={`Send date ${idx + 1}`}
                              onChange={(e) => {
                                const next = [...sendDates];
                                next[idx] = { ...next[idx], date: e.target.value };
                                setSendDates(next);
                              }}
                              w="170px"
                            />
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                              At time (clinic time)
                            </Text>
                            <Input
                              type="time"
                              size="sm"
                              value={r.time}
                              aria-label={`Send time ${idx + 1}`}
                              onChange={(e) => {
                                const next = [...sendDates];
                                next[idx] = { ...next[idx], time: e.target.value };
                                setSendDates(next);
                              }}
                              w="120px"
                            />
                          </Box>
                          {dupDate && (
                            <Text fontSize="xs" color="red.500" flex="1" pb={2}>Duplicate date</Text>
                          )}
                          {isInvalid && !dupDate && (
                            <Text fontSize="xs" color="red.500" flex="1" pb={2}>Date required</Text>
                          )}
                          <IconButton
                            aria-label={`Remove send date ${idx + 1}`}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            icon={<span style={{ fontWeight: 700 }}>×</span>}
                            isDisabled={sendDates.length <= 1}
                            onClick={() => setSendDates(sendDates.filter((x) => x.id !== r.id))}
                          />
                        </HStack>
                      );
                    })}
                  </VStack>
                  <Button
                    mt={2}
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => {
                      // Suggest the day after the last filled date so a
                      // follow-up text is one click away.
                      const filled = sendDates.filter((x) => DATE_RE.test(x.date || ""));
                      const seedDate = filled.length ? shiftDateString(filled[filled.length - 1].date, 1) || "" : "";
                      setSendDates([...sendDates, { id: newRowId(), date: seedDate, time: "09:00" }]);
                    }}
                  >
                    + Add another send date
                  </Button>
                </Box>
              )}
            </VStack>
          </FormControl>

          {/* Window 1: Scheduled (active) */}
          <Box p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={0}>
                <HStack spacing={2} align="center">
                  <Text fontWeight="bold" color="blue.700">Scheduled</Text>
                  <Badge colorScheme="blue">{activeRows.length}</Badge>
                </HStack>
                <Text fontSize="sm" color="blue.700">
                  Queued or processing confirmations for appointments on {date || "this date"}.
                  Times in your local time.
                </Text>
              </VStack>
              <HStack spacing={2}>
                <Button size="sm" variant="outline" colorScheme="blue" onClick={loadSchedule} isLoading={loadingSchedule}>
                  Refresh
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => cancelRows(activeRows.map((r) => r.id), "all")}
                  isLoading={cancelling === "all"}
                  isDisabled={activeRows.length === 0}
                >
                  Cancel all for this day
                </Button>
              </HStack>
            </HStack>
            <Box mt={3} data-testid="confirmation-scheduled">
              {renderScheduledList(activeRows, { isActive: true, emptyText: "No scheduled confirmations for this day." })}
            </Box>
          </Box>

          {/* Window 2: Sent / Cancelled / Failed (history) */}
          <Box p={4} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.300">
            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={0}>
                <HStack spacing={2} align="center">
                  <Text fontWeight="bold" color="gray.700">Sent / Cancelled / Failed</Text>
                  <Badge colorScheme="gray">{historyRows.length}</Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  History of confirmations already delivered, cancelled, or failed
                  for appointments on {date || "this date"}. Times in your local time.
                </Text>
              </VStack>
              <Button size="sm" variant="outline" onClick={loadSchedule} isLoading={loadingSchedule}>
                Refresh
              </Button>
            </HStack>
            <Box mt={3} data-testid="confirmation-history">
              {renderScheduledList(historyRows, { isActive: false, emptyText: "No sent, cancelled, or failed confirmations for this day." })}
            </Box>
          </Box>

          {/* Patient Type Filter */}
          <FormControl>
            <FormLabel>Filter by Patient Type ({typeFilter.length} selected)</FormLabel>
            <VStack
              align="flex-start"
              spacing={2}
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              bg="gray.50"
              maxHeight="200px"
              overflowY="auto"
            >
              {availableTypes.length === 0 ? (
                <Text fontSize="sm" color="gray.500" fontStyle="italic">No patient types found</Text>
              ) : (
                <>
                  <Checkbox
                    isChecked={typeFilter.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) setTypeFilter([]);
                    }}
                  >
                    All Types ({patients.length})
                  </Checkbox>
                  {availableTypes.map((type) => {
                    const typeCount = patients.filter((p) => typeOf(p) === type).length;
                    return (
                      <Checkbox
                        key={type}
                        isChecked={typeFilter.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) setTypeFilter((prev) => [...prev, type]);
                          else setTypeFilter((prev) => prev.filter((t) => t !== type));
                        }}
                      >
                        {type} ({typeCount})
                      </Checkbox>
                    );
                  })}
                </>
              )}
            </VStack>
            <Text fontSize="sm" color="gray.500" mt={2}>
              Showing {filtered.length} of {patients.length} patients
            </Text>
          </FormControl>

          {/* Patient Selection */}
          <FormControl>
            <FormLabel>
              Select Patients to Send Confirmations ({selectedIds.length} selected of {filtered.length} shown)
            </FormLabel>
            <VStack
              align="stretch"
              spacing={3}
              maxHeight="400px"
              overflowY="auto"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              bg="gray.50"
              data-testid="confirmation-patient-list"
            >
              {filtered.length === 0 ? (
                <Center py={8}>
                  <VStack spacing={2}>
                    <Text color="gray.500" fontWeight="semibold">
                      {patients.length === 0
                        ? "No patients scheduled for this date"
                        : `No patients found with selected type(s): ${typeFilter.join(", ")}`}
                    </Text>
                    {patients.length > 0 && (
                      <Text fontSize="sm" color="gray.400">Try selecting different patient type filter(s).</Text>
                    )}
                  </VStack>
                </Center>
              ) : (
                filtered.map((patient) => {
                  if (!patient || !patient.id) return null;
                  const badge = confirmationBadge(patient);
                  const selectable = isSelectable(patient);
                  const displayTime = patient.time_
                    ? convertScheduledTime(patient.date_, patient.time_, patient.timezone_source ?? patient.timezone ?? null, viewerTimezone)
                    : "N/A";
                  return (
                    <Box key={patient.id} p={3} border="1px" borderColor="gray.200" borderRadius="md" bg="white">
                      <HStack justify="space-between">
                        <VStack align="flex-start" spacing={1} flex={1}>
                          <HStack>
                            <Checkbox
                              isChecked={Boolean(selected[patient.id]) && selectable}
                              isDisabled={!selectable}
                              aria-label={`Select ${nameOf(patient)}`}
                              onChange={(e) =>
                                setSelected((prev) => ({ ...prev, [patient.id]: e.target.checked }))
                              }
                            />
                            <Text fontWeight="bold">{nameOf(patient)}</Text>
                            <Text fontSize="sm" color="gray.600">({patient.type || "N/A"})</Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            Time: {displayTime}
                            {patient.confirmation_sent_at
                              ? ` | Asked: ${new Date(patient.confirmation_sent_at).toLocaleDateString()}`
                              : " | Not yet asked"}
                          </Text>
                          <HStack spacing={4} flexWrap="wrap">
                            {hasPhone(patient) ? (
                              <PatientPhones row={patient} compact prefix="📞 " fontSize="sm" color="blue.600" />
                            ) : (
                              <Text fontSize="xs" color="orange.600" fontStyle="italic">
                                {hasEmail(patient) ? "No phone number, will be emailed" : "No phone number"}
                              </Text>
                            )}
                            {hasEmail(patient) && (
                              <Text fontSize="sm" color="gray.600">✉️ {patient.email}</Text>
                            )}
                          </HStack>
                        </VStack>
                        <Badge colorScheme={badge.colorScheme} px={2} py={1} whiteSpace="nowrap">
                          {badge.label}
                        </Badge>
                      </HStack>
                    </Box>
                  );
                })
              )}
            </VStack>
          </FormControl>

          {/* Message Template Selection */}
          <FormControl>
            <FormLabel>Message Template</FormLabel>
            <Select value={template} onChange={(e) => setTemplate(e.target.value)}>
              <option value="default">Default Confirmation</option>
            </Select>
          </FormControl>
        </VStack>
      </ModalBody>
      <ModalFooter>
        {/* Wraps onto a second line on a narrow modal instead of spilling out. */}
        <Flex wrap="wrap" gap={2} justify="flex-end" w="100%" data-testid="confirmation-footer">
          <Button
            variant="outline"
            onClick={() => {
              const all = {};
              filtered.forEach((p) => {
                if (p && p.id && isSelectable(p)) all[p.id] = true;
              });
              setSelected(all);
            }}
          >
            Select All ({selectableCount})
          </Button>
          <Button variant="outline" onClick={() => setSelected({})}>
            Clear All
          </Button>
          <Button
            colorScheme="blue"
            onClick={send}
            isLoading={sending}
            loadingText={scheduleMode ? "Scheduling..." : "Sending..."}
            isDisabled={sending || selectedIds.length === 0}
          >
            {scheduleMode ? "Schedule Confirmations" : "Send Confirmations"}
          </Button>
          <Button
            variant="outline"
            colorScheme="teal"
            onClick={sendEmails}
            isLoading={emailing}
            loadingText="Emailing..."
            isDisabled={emailing || sending || selectedIds.length === 0}
            data-testid="send-email-confirmations"
          >
            Send Email Confirmation
          </Button>
          <Button variant="outline" onClick={previewMessage} isLoading={previewing} isDisabled={filtered.length === 0}>
            Preview Message
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </Flex>
      </ModalFooter>

      <Modal isOpen={Boolean(preview)} onClose={() => setPreview(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{preview?.title || "Message Preview"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              whiteSpace="pre-wrap"
              fontFamily="sans-serif"
              fontSize="sm"
              maxHeight="400px"
              overflowY="auto"
              border="1px"
              borderColor="gray.200"
              data-testid="confirmation-preview-text"
            >
              {preview?.text || "No message to display."}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={() => setPreview(null)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ConfirmationRemindersPanel;
